import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  Chip,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  useMediaQuery,
  Snackbar,
  Link,
  AlertTitle
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import FlightIcon from '@mui/icons-material/Flight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import InterestsIcon from '@mui/icons-material/Interests';
import IosShareIcon from '@mui/icons-material/IosShare';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { motion } from 'framer-motion';
import { format, addDays, differenceInDays, isValid } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DateRangePicker from '../components/DateRangePicker';
import { generateItinerary as generateItineraryFromService } from '../services/openaiService';
import { 
  saveItinerary as saveItineraryService, 
  updateItinerary,
  deleteItinerary
} from '../services/itineraryService';
import { getDestinationImage, DEFAULT_DESTINATION_IMAGE } from '../utils/imageUtils';
import { createShareLink, getSharedItinerary } from '../services/shareService';
import { useAuth } from '../contexts/AuthContext';
import { printItinerary } from '../utils/printUtils';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { createMapLink } from '../utils/linkUtils';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TokenDisplay from '../components/TokenDisplay';
import TokenIcon from '@mui/icons-material/Token';

// Helper function to safely format dates
const safeFormatDate = (date, formatString) => {
  if (!date) return 'N/A';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  return isValid(dateObj) ? format(dateObj, formatString) : 'Invalid date';
};

// Helper function to normalize budget values to one of the three allowed options
const normalizeBudget = (budgetValue) => {
  // Default to medium if no value
  if (!budgetValue) return 'medium';
  
  // Convert to lowercase for case-insensitive comparison
  const lowercaseBudget = budgetValue.toLowerCase();
  
  // Map various possible values to our three standardized options
  if (lowercaseBudget === 'budget' || lowercaseBudget === 'economy' || lowercaseBudget === 'low') {
    return 'budget';
  } else if (lowercaseBudget === 'luxury' || lowercaseBudget === 'high' || lowercaseBudget === 'premium') {
    return 'luxury';
  } else {
    // Default all other values to medium (moderate, standard, etc.)
    return 'medium';
  }
};

const ItineraryPage = ({ 
  initialTripDetails = null, 
  itinerary: initialItinerary = null, 
  onEditTrip = null, 
  isSharedView = false, 
  isSavedView = false,
  itineraryId: initialItineraryId = null 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(!initialItinerary);
  const [error, setError] = useState(null);
  const [itinerary, setItinerary] = useState(initialItinerary);
  const [currentDay, setCurrentDay] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [destinationImage, setDestinationImage] = useState(DEFAULT_DESTINATION_IMAGE);
  const [saveStatus, setSaveStatus] = useState({ 
    saving: false, 
    saved: false, 
    wasSaved: false, 
    error: null 
  });
  const [saveSuccessDialogOpen, setSaveSuccessDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkExpiry, setShareLinkExpiry] = useState('7d');
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [shareSnackbarMessage, setShareSnackbarMessage] = useState('');
  const [showSavedAlert, setShowSavedAlert] = useState(true);
  
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get('edit');
  const shareId = queryParams.get('shareId');
  // Use the prop value or fallback to checking for shareId
  const isSharedMode = isSharedView || Boolean(shareId);
  
  console.log("ItineraryPage initialization - isSharedView:", isSharedView, "shareId:", shareId, "isSharedMode:", isSharedMode);
  
  // Trip details state
  const [tripDetails, setTripDetails] = useState(() => {
    const initialDetails = initialTripDetails || {
      destination: queryParams.get('destination') || 'Paris, France',
      startDate: queryParams.get('startDate') ? new Date(queryParams.get('startDate')) : new Date(),
      endDate: queryParams.get('endDate') ? new Date(queryParams.get('endDate')) : addDays(new Date(), 3),
      travelers: parseInt(queryParams.get('travelers')) || 2,
      adults: initialTripDetails?.adults || parseInt(queryParams.get('adults')) || 2,
      children: initialTripDetails?.children || parseInt(queryParams.get('children')) || 0,
      budget: queryParams.get('budget') || 'medium',
      interests: queryParams.get('interests')?.split(',').filter(i => i) || ['Food & Dining', 'History & Culture'],
    };
    
    // Normalize budget to one of the three allowed values
    return {
      ...initialDetails,
      budget: initialDetails.budget ? normalizeBudget(initialDetails.budget) : 'medium'
    };
  });
  
  // For editing interests as a string
  const [interestsString, setInterestsString] = useState(
    Array.isArray(tripDetails.interests) 
      ? tripDetails.interests.join(', ')
      : typeof tripDetails.interests === 'string'
        ? tripDetails.interests
        : ''
  );
  
  // Setup ref for print state
  const printContainerRef = useRef(null);
  
  const { user } = useAuth();
  
  // Add a flag to prevent multiple generation calls
  const isGeneratingRef = useRef(false);
  
  // Add a flag to track if this is the first load
  const [firstLoad, setFirstLoad] = useState(true);
  
  // useEffect to auto-save on first successful load of the itinerary
  useEffect(() => {
    // Only trigger auto-save if:
    // 1. We have a user (can't save without being logged in)
    // 2. We have an itinerary
    // 3. We're not already in a loading or error state
    // 4. We're not in shared or already-saved view
    // 5. It's the first successful load
    if (user && itinerary && !loading && !error && !isSharedMode && !isSavedView && firstLoad) {
      console.log("Auto-saving itinerary on first successful load");
      // Use a delay to ensure UI is fully loaded
      const timer = setTimeout(() => {
        handleSaveItinerary();
        setFirstLoad(false); // Prevent further auto-saves
      }, 2000);
      
      return () => clearTimeout(timer); // Clean up the timer
    }
  }, [itinerary, loading, user, error, isSharedMode, isSavedView, firstLoad]);
  
  const generateItinerary = async () => {
    // Prevent multiple concurrent calls
    if (isGeneratingRef.current) {
      console.log("Generation already in progress, skipping duplicate call");
      return;
    }
    
    try {
      isGeneratingRef.current = true;
      setLoading(true);
      
      // Reset save status for the new itinerary
      setSaveStatus({ saving: false, saved: false, wasSaved: false, error: null });
      
      // Call the generateItinerary function from the openaiService
      const newItinerary = await generateItineraryFromService(tripDetails);
      console.log("Generated new itinerary:", newItinerary);
      
      setItinerary(newItinerary);
      setError(null);
    } catch (err) {
      console.error('Error generating itinerary:', err);
      setError('Failed to generate itinerary. Please try again.');
    } finally {
      setLoading(false);
      isGeneratingRef.current = false;
    }
  };
  
  const normalizeItineraryData = (data) => {
    if (!data) return null;
    
    console.log("Normalizing itinerary data:", data);
    
    // Create a deep copy to avoid mutating the original data
    let normalizedData = JSON.parse(JSON.stringify(data));
    
    // Extract travel info from various possible locations
    const travelInfo = 
      data.travelInfo || 
      data.itineraryData?.travelInfo || 
      (data.itineraryData?.itineraryData?.travelInfo) || 
      null;
    
    console.log("Found travelInfo:", travelInfo);
    
    // Process the shared format where data is nested
    if (normalizedData.itineraryData) {
      console.log("Found nested itineraryData structure, flattening");
      
      // If it's a complex shared itinerary model, normalize it more gracefully
      normalizedData = {
        ...normalizedData,
        summary: normalizedData.itineraryData.summary || "",
        days: normalizedData.itineraryData.days || [],
        tips: normalizedData.itineraryData.tips || [],
        travelInfo: travelInfo
      };
    } else {
      // Ensure we have travelInfo in our normalized data
      normalizedData.travelInfo = travelInfo;
    }
    
    // Parse dates if they are strings
    if (typeof normalizedData.startDate === 'string') {
      normalizedData.startDate = new Date(normalizedData.startDate);
      console.log("Converted startDate to Date object:", normalizedData.startDate);
    }
    if (typeof normalizedData.endDate === 'string') {
      normalizedData.endDate = new Date(normalizedData.endDate);
      console.log("Converted endDate to Date object:", normalizedData.endDate);
    }
    
    // Normalize days and activities
    if (normalizedData.days) {
      normalizedData.days = normalizedData.days.map(day => {
        // Convert day.date to Date object if it's a string
        let dayDate = day.date;
        if (typeof dayDate === 'string') {
          dayDate = new Date(dayDate);
          console.log(`Converted day ${day.day} date from ${day.date} to Date object:`, dayDate);
        }
        
        // Ensure day has all required properties
        return {
          ...day,
          date: dayDate,
          activities: day.activities ? day.activities.map(activity => ({
            ...activity,
            type: activity.type || 'activity'
          })) : []
        };
      });
      
      console.log("Normalized days with Date objects:", normalizedData.days.map(d => ({
        day: d.day,
        date: d.date
      })));
    }
    
    return normalizedData;
  };
  
  const fetchSharedItinerary = async () => {
    console.log("=== fetchSharedItinerary called - shareId:", shareId, "itinerary:", !!itinerary, "isSharedView:", isSharedView, "isSharedMode:", isSharedMode);
    if (shareId && !itinerary) {
      try {
        setLoading(true);
        setError(null);
        
        console.log("Fetching shared itinerary with ID:", shareId);
        const sharedItinerary = await getSharedItinerary(shareId);
        console.log("DEBUG: Received shared itinerary:", JSON.stringify(sharedItinerary, null, 2));
        
        if (sharedItinerary) {
          try {
            // Normalize the data structure
            const normalizedItinerary = normalizeItineraryData(sharedItinerary);
            console.log("DEBUG: Normalized itinerary:", JSON.stringify(normalizedItinerary, null, 2));
            
            // Validate the itinerary has at least some minimal data
            if (!normalizedItinerary.destination) {
              throw new Error("Shared itinerary is missing destination information");
            }
            
            setItinerary(normalizedItinerary);
            setTripDetails({
              destination: normalizedItinerary.destination || 'Unknown Destination',
              startDate: new Date(normalizedItinerary.startDate || new Date()),
              endDate: new Date(normalizedItinerary.endDate || new Date()),
              travelers: normalizedItinerary.travelers || 1,
              adults: normalizedItinerary.adults || normalizedItinerary.travelers || 1,
              children: normalizedItinerary.children || 0,
              budget: normalizeBudget(normalizedItinerary.budget || 'Medium'),
              interests: normalizedItinerary.interests || []
            });
            
            // Reset to first day for safety
            setCurrentDay(0);
            
            // Set destination image based on the itinerary
            if (normalizedItinerary.destination) {
              const imageUrl = getDestinationImage(normalizedItinerary.destination);
              setDestinationImage(imageUrl);
            }
          } catch (error) {
            console.error("Error processing shared itinerary:", error);
            setError("The shared itinerary appears to be invalid or corrupted.");
          }
        } else {
          setError("Shared itinerary not found or has expired.");
        }
      } catch (error) {
        console.error("Error fetching shared itinerary:", error);
        setError("Failed to load the shared itinerary. It may have expired or been removed.");
      } finally {
        setLoading(false);
      }
    }
  };
  
  useEffect(() => {
    // Set destination image based on either the itinerary destination or tripDetails destination
    if (itinerary?.destination) {
      const imageUrl = getDestinationImage(itinerary.destination);
      setDestinationImage(imageUrl);
    } else if (tripDetails?.destination) {
      const imageUrl = getDestinationImage(tripDetails.destination);
      setDestinationImage(imageUrl);
    }
  }, [itinerary?.destination, tripDetails?.destination]);
  
  useEffect(() => {
    if (!itinerary && !shareId && !isSharedView && !isSharedMode && !isGeneratingRef.current) {
      generateItinerary();
    }
  }, [itinerary, generateItinerary, shareId, isSharedView, isSharedMode]);
  
  // Check if this is a shared itinerary (handles both shareId and isSharedView)
  const fetchedRef = useRef(false);
  useEffect(() => {
    if ((shareId || isSharedView) && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSharedItinerary();
    }
  }, [shareId, isSharedView]);
  
  // Automatically generate itinerary when user submits valid trip details
  useEffect(() => {
    if (tripDetails && queryParams.has('generate') && !loading && !isGeneratingRef.current) {
      isGeneratingRef.current = true;
      setLoading(true);
      setTimeout(() => {
        generateItinerary();
      }, 1000);
    }
  }, [tripDetails, queryParams, loading]);
  
  const handleEditDialogOpen = () => {
    setInterestsString(tripDetails.interests.join(', '));
    // Initialize any validation state we need
    setFormErrors({});
    
    // Ensure budget is normalized to one of the three allowed values
    setTripDetails(prev => ({
      ...prev,
      budget: normalizeBudget(prev.budget)
    }));
    
    setEditDialogOpen(true);
  };
  
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };
  
  // Add state for validation
  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({
    startDate: false,
    endDate: false
  });
  
  const handleTripDetailsChange = (field, value) => {
    setTripDetails(prev => {
      // Create updated trip details
      const updated = {
        ...prev,
        [field]: field === 'budget' ? normalizeBudget(value) : value
      };
      
      // If adults or children changes, update the total travelers count
      if (field === 'adults' || field === 'children') {
        updated.travelers = (updated.adults || 0) + (updated.children || 0);
      }
      
      return updated;
    });
    
    // Mark field as touched for validation
    if (field === 'startDate' || field === 'endDate') {
      setFormTouched(prev => ({
        ...prev,
        [field]: true
      }));
    }
    
    // Validate after change
    validateForm();
  };
  
  // Handle date range changes from the DateRangePicker
  const handleDateRangeChange = (startDate, endDate) => {
    setTripDetails(prev => ({
      ...prev,
      startDate,
      endDate
    }));
    
    // Mark dates as touched
    setFormTouched(prev => ({
      ...prev,
      startDate: true,
      endDate: endDate !== null
    }));
    
    // Validate after change
    validateForm();
  };
  
  // Validate form and update error state
  const validateForm = () => {
    const errors = {};
    
    // Only validate required fields if they're empty
    if (!tripDetails.destination) {
      errors.destination = 'Destination is required';
    }
    
    if (!tripDetails.startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!tripDetails.endDate) {
      errors.endDate = 'End date is required';
    }
    
    // Validate date range doesn't exceed two weeks (14 days)
    if (tripDetails.startDate instanceof Date && tripDetails.endDate instanceof Date) {
      const daysDifference = differenceInDays(tripDetails.endDate, tripDetails.startDate);
      
      // Only show error if the difference is significantly over the limit (to avoid edge cases)
      if (daysDifference > 14) {
        errors.dateRange = 'Trip duration cannot exceed 14 days';
      }
    }
    
    setFormErrors(errors);
    // If there are critical errors preventing generation, return false
    const criticalErrors = ['destination', 'startDate', 'endDate'].some(field => errors[field]);
    return !criticalErrors;
  };
  
  const handleSaveChanges = async () => {
    // Prevent multiple calls
    if (isGeneratingRef.current) {
      return;
    }
    
    // Validate the form before proceeding
    if (!validateForm()) {
      console.error("Form validation failed");
      return;
    }
    
    try {
      // Process interests string into array
      const interestsArray = interestsString
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== '');
      
      // Update trip details with new interests
      const updatedDetails = {
        ...tripDetails,
        interests: interestsArray,
        // Include the itineraryId for regeneration token handling
        itineraryId: itineraryId
      };
      
      console.log("Date information before regenerating:", {
        startDate: updatedDetails.startDate,
        startDateIso: updatedDetails.startDate.toISOString(),
        endDate: updatedDetails.endDate,
        endDateIso: updatedDetails.endDate.toISOString(),
        // Log itineraryId to confirm it's being included
        itineraryId: updatedDetails.itineraryId
      });
      
      setTripDetails(updatedDetails);
      setEditDialogOpen(false);
      
      // Reset current day to 0 to avoid "day" property access issues
      setCurrentDay(0);
      
      // Reset save status since we're generating a new itinerary
      setSaveStatus({ saving: false, saved: false, wasSaved: false, error: null });
      
      // Set the generation flag to prevent duplicate calls
      isGeneratingRef.current = true;
      
      // Set loading state before regenerating
      setLoading(true);
      setItinerary(null); // Clear existing itinerary while loading
      
      try {
        // Call the generateItinerary function from the openaiService
        const newItinerary = await generateItineraryFromService(updatedDetails);
        console.log("Generated new itinerary:", newItinerary);
        console.log("Date display debug:", {
          originalStartDate: updatedDetails.startDate,
          originalEndDate: updatedDetails.endDate,
          newStartDate: newItinerary.startDate,
          newEndDate: newItinerary.endDate,
          isRegeneration: newItinerary.isRegeneration
        });
        
        // Ensure the dates in the new itinerary match exactly what was selected
        newItinerary.startDate = updatedDetails.startDate;
        newItinerary.endDate = updatedDetails.endDate;
        
        setItinerary(newItinerary);
        setError(null);

        // Auto-save the regenerated itinerary
        console.log("Auto-saving regenerated itinerary");
        setTimeout(() => {
          // Make sure we're saving with the latest trip details
          handleSaveItinerary(newItinerary, updatedDetails);
        }, 1500);

      } catch (err) {
        console.error('Error regenerating itinerary:', err);
        setError('Failed to regenerate itinerary. Please try again.');
      } finally {
        setLoading(false);
        isGeneratingRef.current = false;
      }
    } catch (err) {
      console.error('Error handling save changes:', err);
      setError('Failed to update trip details. Please try again.');
      isGeneratingRef.current = false;
    }
  };
  
  const handlePrint = () => {
    printItinerary(itinerary, user);
  };
  
  const handleTabChange = (event, newValue) => {
    setCurrentDay(newValue);
  };
  
  const handlePlanAnotherTrip = () => {
    console.log("handlePlanAnotherTrip called", { onEditTrip: !!onEditTrip });
    
    try {
      // Create a clean object with properly formatted dates
      const cleanTripDetails = {
        destination: tripDetails.destination || '',
        // Use toISOString() for consistent date serialization
        startDate: new Date().toISOString(), // Default as fallback
        endDate: addDays(new Date(), 3).toISOString(), // Default as fallback
        travelers: parseInt(tripDetails.travelers) || 2,
        adults: parseInt(tripDetails.adults) || 2,
        children: parseInt(tripDetails.children) || 0,
        budget: tripDetails.budget || 'medium',
        interests: Array.isArray(tripDetails.interests) ? 
          tripDetails.interests : 
          (typeof tripDetails.interests === 'string' ? 
            tripDetails.interests.split(',').map(i => i.trim()) : 
            ['Food & Dining', 'History & Culture'])
      };
      
      // If we have valid dates, use them instead of defaults
      if (tripDetails.startDate) {
        try {
          const startDate = new Date(tripDetails.startDate);
          if (!isNaN(startDate.getTime())) {
            cleanTripDetails.startDate = startDate.toISOString();
          }
        } catch (e) {
          console.error("Error parsing start date:", e);
        }
      }
      
      if (tripDetails.endDate) {
        try {
          const endDate = new Date(tripDetails.endDate);
          if (!isNaN(endDate.getTime())) {
            cleanTripDetails.endDate = endDate.toISOString();
          }
        } catch (e) {
          console.error("Error parsing end date:", e);
        }
      }
      
      console.log("Clean trip details:", cleanTripDetails);
      
      if (onEditTrip && typeof onEditTrip === 'function') {
        console.log("Calling onEditTrip function with clean data");
        onEditTrip(cleanTripDetails);
      } else {
        console.log("Navigating to /planner with clean data");
        navigate('/planner', { state: { tripDetails: cleanTripDetails } });
      }
    } catch (error) {
      console.error("Error in handlePlanAnotherTrip:", error);
      // Fallback navigation with minimal data
      navigate('/planner', { 
        state: { 
          tripDetails: {
            destination: tripDetails.destination || '',
            startDate: new Date().toISOString(),
            endDate: addDays(new Date(), 3).toISOString()
          }
        }
      });
    }
  };
  
  // State for tracking if this is an update to an existing itinerary
  const [itineraryId, setItineraryId] = useState(initialItineraryId || null);
  
  // Add a handler to save the current itinerary
  const handleSaveItinerary = async (customItinerary = null, customTripDetails = null) => {
    try {
      const wasPreviouslySaved = saveStatus.saved;
      setSaveStatus({ 
        saving: true, 
        saved: false,
        wasSaved: wasPreviouslySaved,
        error: null 
      });
      
      // Use the custom values if provided, otherwise use the state values
      const itineraryToUse = customItinerary || itinerary;
      const tripDetailsToUse = customTripDetails || tripDetails;
      
      // Ensure days array has properly formatted dates
      const processedDays = itineraryToUse.days?.map(day => {
        // Make sure each day has a date that is a Date object
        const dayDate = day.date instanceof Date ? day.date : new Date(day.date);
        return {
          ...day,
          date: dayDate
        };
      }) || [];
      
      console.log('Processed days for save:', processedDays.map(d => ({
        day: d.day,
        date: d.date
      })));
      
      // Prepare the itinerary data for saving
      const itineraryToSave = {
        title: `${itineraryToUse.destination} Trip - ${safeFormatDate(itineraryToUse.startDate, 'MMM d')} to ${safeFormatDate(itineraryToUse.endDate, 'MMM d')}`,
        destination: itineraryToUse.destination,
        startDate: itineraryToUse.startDate,
        endDate: itineraryToUse.endDate,
        travelers: tripDetailsToUse.travelers,
        adults: tripDetailsToUse.adults || tripDetailsToUse.travelers,
        children: tripDetailsToUse.children || 0,
        budget: tripDetailsToUse.budget,
        interests: tripDetailsToUse.interests,
        itineraryData: {
          days: processedDays,
          summary: itineraryToUse.summary,
          tips: itineraryToUse.tips,
          travelInfo: itineraryToUse.travelInfo
        },
        isFavorite: false,
        isPublic: false
      };
      
      console.log('Saving itinerary with dates:', {
        startDate: itineraryToSave.startDate,
        endDate: itineraryToSave.endDate,
        firstDayDate: itineraryToSave.itineraryData.days[0]?.date
      });
      
      let result;
      
      // Check if we're updating an existing itinerary or creating a new one
      if (itineraryId) {
        // Update existing itinerary
        result = await updateItinerary(itineraryId, itineraryToSave);
        console.log('Itinerary updated successfully:', result);
      } else {
        // Create new itinerary
        result = await saveItineraryService(itineraryToSave);
        console.log('Itinerary saved successfully:', result);
        // Save the new ID
        if (result && result.id) {
          setItineraryId(result.id);
        }
      }
      
      // Update the save status
      setSaveStatus({ 
        saving: false, 
        saved: true,
        wasSaved: wasPreviouslySaved,
        error: null 
      });
    } catch (error) {
      console.error('Error saving itinerary:', error);
      setSaveStatus({ 
        saving: false, 
        saved: false, 
        wasSaved: saveStatus.wasSaved,
        error: error.message || 'Failed to save itinerary. Please try again.'
      });
    }
  };
  
  // Add functions for sharing
  const handleShareDialogOpen = () => {
    setShareDialogOpen(true);
  };
  
  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
  };
  
  const handleGenerateShareLink = async () => {
    if (!itinerary) return;
    
    try {
      setShareLoading(true);
      
      // Create a share object with necessary data
      const shareData = {
        itineraryId: itineraryId, // If saved
        itineraryData: itinerary,
        expiresIn: shareLinkExpiry
      };
      
      // Call API to create share link
      const result = await createShareLink(shareData);
      setShareLink(result.shareUrl);
    } catch (error) {
      console.error('Error generating share link:', error);
      setShareSnackbarMessage('Failed to generate share link. Please try again.');
      setShareSnackbarOpen(true);
    } finally {
      setShareLoading(false);
    }
  };
  
  const handleCopyShareLink = () => {
    if (!shareLink) return;
    
    // Check if the Clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareLink)
        .then(() => {
          setShareSnackbarMessage('Link copied to clipboard!');
          setShareSnackbarOpen(true);
        })
        .catch(err => {
          console.error('Failed to copy link:', err);
          setShareSnackbarMessage('Failed to copy link.');
          setShareSnackbarOpen(true);
        });
    } else {
      // Fallback method for browsers that don't support the Clipboard API
      try {
        // Create a temporary textarea element
        const textArea = document.createElement('textarea');
        textArea.value = shareLink;
        
        // Make the textarea out of viewport
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        
        // Select and copy the text
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        
        // Remove the temporary element
        document.body.removeChild(textArea);
        
        if (successful) {
          setShareSnackbarMessage('Link copied to clipboard!');
        } else {
          setShareSnackbarMessage('Please copy the link manually.');
        }
        setShareSnackbarOpen(true);
      } catch (err) {
        console.error('Fallback copy method failed:', err);
        setShareSnackbarMessage('Please copy the link manually.');
        setShareSnackbarOpen(true);
      }
    }
  };
  
  const handleShareSocial = (platform) => {
    if (!shareLink) return;
    
    let shareUrl;
    const text = `Check out my travel itinerary for ${itinerary.destination}!`;
    
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareLink)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(text)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareLink)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };
  
  const handleToggleFavorite = async () => {
    if (!itinerary) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const updatedItinerary = {
        ...itinerary,
        isFavorite: !itinerary.isFavorite
      };
      
      const result = await updateItinerary(itineraryId, updatedItinerary);
      console.log('Itinerary favorite status updated successfully:', result);
      
      setItinerary(updatedItinerary);
      setError(null);
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      setError('Failed to update favorite status. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteItinerary = async () => {
    if (!itinerary || !itineraryId) return;
    
    // Confirm with the user before deleting
    if (!window.confirm("Are you sure you want to delete this itinerary? This action cannot be undone.")) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Call the delete API
      const result = await deleteItinerary(itineraryId);
      console.log('Itinerary deleted successfully:', result);
      
      // Redirect to saved trips page
      navigate('/saved-trips');
    } catch (error) {
      console.error('Error deleting itinerary:', error);
      setError('Failed to delete itinerary. Please try again.');
      setLoading(false);
    }
  };
  
  // Add after the state declarations
  const loadingMessages = [
    { icon: '🗺️', message: 'Finding points of interest...' },
    { icon: '🚗', message: 'Calculating travel distances...' },
    { icon: '🍽️', message: 'Discovering local restaurants...' },
    { icon: '🎭', message: 'Planning activities...' },
    { icon: '🏨', message: 'Finding accommodations...' },
    { icon: '📅', message: 'Organizing your schedule...' }
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Add the loading message rotation effect
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setCurrentMessageIndex((prevIndex) => 
          prevIndex === loadingMessages.length - 1 ? 0 : prevIndex + 1
        );
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [loading]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: 'calc(100vh - 64px)',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)), url(${destinationImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        px: { xs: 2, sm: 4 },
        textAlign: 'center'
      }}>
        <Box
          component={motion.div}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '100%',
            width: '400px',
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(10px)',
            p: 4,
            borderRadius: 2
          }}
        >
          <CircularProgress size={48} sx={{ color: 'white' }} />
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              color: 'white',
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              px: { xs: 1, sm: 2 },
              wordBreak: 'break-word'
            }}
          >
            Creating your dream trip to {tripDetails.destination}
          </Typography>

          <Box
            component={motion.div}
            key={currentMessageIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}
            >
              {loadingMessages[currentMessageIndex].icon}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              {loadingMessages[currentMessageIndex].message}
            </Typography>
          </Box>

          <Typography 
            variant="caption" 
            sx={{ 
              mt: 3,
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.75rem', sm: '0.8rem' },
              maxWidth: '280px'
            }}
          >
            This usually takes about a minute. We're making sure every detail is perfect!
          </Typography>
        </Box>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ pt: 10, pb: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        {!isSharedMode && (
        <Button 
          variant="contained" 
          onClick={generateItinerary}
          startIcon={<RefreshIcon />}
        >
          Try Again
        </Button>
        )}
      </Container>
    );
  }
  
  return (
    <Box sx={{ mt: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 1, pt: { xs: 5, sm: 6 } }}>
          {/* Trip Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            mb: { xs: 2, md: 3 }
          }}>
            <Box sx={{ 
              mb: { xs: 2, md: 3 }, 
              position: 'relative', 
              zIndex: 1,
              width: '100%',
              mt: { xs: 0, sm: 1 }
            }}>
              {/* Add TokenDisplay to top right - only for active view */}
              {!isSharedMode && !isSavedView && (
                <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 8, md: 12 },  // Adjusted top spacing
                  right: { xs: 12, md: 16 }, // Adjusted right spacing
                  zIndex: 100,
                  transform: 'scale(0.85)', // Make it slightly smaller
                  transformOrigin: 'top right', // Scale from top right
                  '& .MuiChip-root': { // Target the chip component inside TokenDisplay
                    height: '28px', // Reduce height
                    '& .MuiChip-label': {
                      fontSize: '0.8rem', // Smaller font size
                      px: 1.5, // Reduce horizontal padding
                    },
                    '& .MuiChip-icon': {
                      fontSize: '1.1rem', // Smaller icon
                      marginLeft: '6px', // Adjust icon spacing
                    },
                  }
                }}
                >
                  <TokenDisplay refreshTrigger={Date.now()} />
                </Box>
              )}
              
              <Typography 
                variant="h4" 
                component="h1" 
                fontWeight={700}
                sx={{ 
                  mb: 1.5, 
                  background: 'linear-gradient(135deg, #15192E 0%, #5E17EB 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  display: 'inline-block',
                  textAlign: 'left',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60px',
                    height: '4px',
                    background: 'linear-gradient(90deg, rgba(94, 23, 235, 0.9) 0%, rgba(94, 23, 235, 0.3) 100%)',
                  }
                }}
              >
                {isSharedMode ? 'Shared Itinerary' : 'Your Itinerary'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 4,
            '@media print': {
              display: 'none'
            }
          }}>
            <Box
              sx={{
                width: '100%',
                p: { xs: 2, sm: 2.5 },
                pt: { xs: 2.5, sm: 2.5 },
                pb: { xs: 1.5, sm: 2.5 },
                borderRadius: '4px',
                bgcolor: 'rgba(94, 23, 235, 0.08)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(94, 23, 235, 0.12)',
                boxShadow: '0 4px 20px rgba(94, 23, 235, 0.08)',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(94, 23, 235, 0.15)'
                }
              }}
            >
              {/* Action Buttons - Moved to top right */}
              {!isSavedView && (
                <Box
                  sx={{
                    display: 'flex',
                    position: { xs: 'absolute', sm: 'absolute' },
                    top: { xs: '8px', sm: '12px' },
                    right: { xs: '8px', sm: '16px' },
                    zIndex: 2,
                    gap: 0.5
                  }}
                >
                  {!isSharedMode && !isSavedView && (
                    <>
                      <Tooltip title="Edit Trip Details">
                        <IconButton
                          color="primary"
                          onClick={handleEditDialogOpen}
                          size="small"
                          sx={{ 
                            color: 'primary.main',
                            p: 0.5,
                            '&:hover': {
                              color: 'primary.dark',
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  
                  {!isSharedMode && (
                    <>
                      <Tooltip title="Share Itinerary">
                        <IconButton
                          color="primary"
                          onClick={handleShareDialogOpen}
                          size="small"
                          sx={{ 
                            color: 'primary.main',
                            p: 0.5,
                            '&:hover': {
                              color: 'primary.dark',
                            }
                          }}
                        >
                          <IosShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  
                  <Tooltip title="Print Itinerary">
                    <IconButton
                      color="primary"
                      onClick={handlePrint}
                      size="small"
                      sx={{ 
                        color: 'primary.main',
                        p: 0.5,
                        '&:hover': {
                          color: 'primary.dark',
                        }
                      }}
                    >
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 1 },
                pr: { xs: '40px', sm: '120px' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1, sm: 1 },
                  flexWrap: 'wrap',
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'primary.main' }}>
                    {itinerary.destination}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 2 },
                    width: { xs: '100%', sm: 'auto' },
                    mt: { xs: 0, sm: 0 }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {itinerary.startDate instanceof Date ? 
                          format(itinerary.startDate, 'MMM d') : 
                          safeFormatDate(itinerary.startDate, 'MMM d')
                        } - {
                          itinerary.endDate instanceof Date ? 
                          format(itinerary.endDate, 'MMM d, yyyy') : 
                          safeFormatDate(itinerary.endDate, 'MMM d, yyyy')
                        }
                        {' '} · {' '}
                        {itinerary?.startDate && itinerary?.endDate ? 
                          (itinerary.startDate instanceof Date && itinerary.endDate instanceof Date ?
                            differenceInDays(itinerary.endDate, itinerary.startDate) + 1 :
                            differenceInDays(new Date(itinerary.endDate), new Date(itinerary.startDate)) + 1
                          ) : '?'} days
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GroupIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {itinerary.travelers} {itinerary.travelers === 1 ? 'person' : 'people'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AttachMoneyIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'primary.main' }} />
                      <Typography variant="body2">
                        {itinerary.budget ? itinerary.budget.charAt(0).toUpperCase() + itinerary.budget.slice(1) : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {itinerary.interests && Array.isArray(itinerary.interests) && itinerary.interests.length > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'row', sm: 'row' },
                      width: { xs: '100%', sm: 'auto' },
                      gap: { xs: 0.5, sm: 1 },
                      mt: { xs: 0, sm: 0 },
                      flexWrap: 'wrap'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InterestsIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 0.5
                      }}>
                        {itinerary.interests.slice(0, isSmallScreen ? 1 : 2).map((interest, index) => (
                          <Chip 
                            key={index} 
                            label={interest} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            sx={{ borderRadius: '4px', height: '20px', '& .MuiChip-label': { py: 0 } }}
                          />
                        ))}
                        {itinerary.interests.length > (isSmallScreen ? 1 : 2) && (
                          <Chip 
                            label={`+${itinerary.interests.length - (isSmallScreen ? 1 : 2)}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined" 
                            sx={{ borderRadius: '4px', height: '20px', '& .MuiChip-label': { py: 0 } }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Notice about potential day mismatch - moved below Trip Summary and made less intrusive */}
          {!isSharedMode && itinerary && 
            (itinerary.days?.length < Math.ceil(differenceInDays(new Date(itinerary.endDate), new Date(itinerary.startDate))) + 1) && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': { fontSize: '0.75rem' },
                '& .MuiAlertTitle-root': { fontSize: '0.80rem' }
              }}
            >
               Sometimes our AI may generate fewer days than requested. If your itinerary doesn't cover your entire trip, <Link 
                component="button" 
                onClick={handleEditDialogOpen}
                sx={{ 
                  fontWeight: 'medium',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  fontSize: 'inherit'
                }}
              >
                Click here
              </Link> to regenerate again.
            </Alert>
          )}
          
          {/* Message about accessing saved itineraries */}
          {!isSharedMode && !isSavedView && user && showSavedAlert && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3,
                '& .MuiAlert-message': { fontSize: '0.75rem' },
                '& .MuiAlertTitle-root': { fontSize: '0.80rem' },
                position: 'relative',
                pr: 4 // Add padding-right to make room for the close icon
              }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setShowSavedAlert(false)}
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              Your itineraries are automatically saved and can be accessed anytime from the <Link 
                component="button" 
                onClick={() => navigate('/saved-trips')}
                sx={{ 
                  fontWeight: 'medium',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  fontSize: 'inherit'
                }}
              >
                My Trips
              </Link> section.
            </Alert>
          )}
          
          {/* Day Tabs */}
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            mb: 3,
            '@media print': {
              display: 'none'
            }
          }}>
            <Tabs 
              value={currentDay} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{ 
                style: { 
                  backgroundColor: 'transparent' 
                }
              }}
              sx={{
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 80,
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease',
                  fontSize: '0.95rem',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  }
                }
              }}
            >
              {itinerary.days && Array.isArray(itinerary.days) ? itinerary.days.map((day, index) => {
                // Calculate the correct date for this day
                const dayDate = itinerary.startDate instanceof Date ? 
                  new Date(new Date(itinerary.startDate).setDate(itinerary.startDate.getDate() + index)) :
                  null;
                
                return (
                  <Tab 
                    key={index} 
                    label={
                      dayDate ? 
                        format(dayDate, 'MMM d') : 
                        safeFormatDate(day?.date, 'MMM d')
                    }
                    id={`day-tab-${index}`}
                    aria-controls={`day-tabpanel-${index}`}
                  />
                );
              }) : <Tab label="No days available" />}
            </Tabs>
          </Box>
          
          {/* Itinerary Content */}
          <Box sx={{ mb: 4 }}>
          
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                {itinerary?.days && 
                  itinerary.days.length > 0 && 
                  currentDay < itinerary.days.length && 
                  itinerary.days[currentDay]?.activities && 
                Array.isArray(itinerary.days[currentDay]?.activities) ? (
                  itinerary.days[currentDay]?.activities.map((activity, index) => (
                      <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                          <Card elevation={1} sx={{ 
                            mb: 1, 
                            borderRadius: 0,
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(94, 23, 235, 0.15)',
                              transform: 'translateY(-2px)'
                            }
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ListItemIcon sx={{ minWidth: '40px' }}>
                                  {activity.type === 'restaurant' ? (
                                    <RestaurantIcon color="primary" />
                                  ) : activity.type === 'hotel' ? (
                                    <HotelIcon color="primary" />
                                  ) : activity.type === 'sightseeing' ? (
                                    <DirectionsWalkIcon color="primary" />
                                  ) : activity.type === 'attraction' ? (
                                    <LocalActivityIcon color="primary" />
                                  ) : activity.type === 'transportation' ? (
                                    <FlightIcon color="primary" />
                                  ) : (
                                    <PlaceIcon color="primary" />
                                  )}
                                </ListItemIcon>
                                <Box sx={{ flex: 1 }}>
                                  {createMapLink(activity.title, itinerary.destination) ? (
                                    <Tooltip title="View on map">
                                      <Link
                                        href={createMapLink(activity.title, itinerary.destination)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        sx={{
                                          color: 'text.primary',
                                          '&:hover': { color: 'primary.main' },
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                                          {activity.title}
                                        </Typography>
                                        <PlaceIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.7 }} />
                                      </Link>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                                      {activity.title}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" color="text.secondary">
                                    {activity.time && (
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        {activity.time}
                                      </Box>
                                    )}
                                    {activity.location && (
                                      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                        {createMapLink(activity.location, itinerary.destination) ? (
                                          <Tooltip title="Open in Google Maps">
                                            <Link
                                              href={createMapLink(activity.location, itinerary.destination)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              underline="hover"
                                              sx={{
                                                color: 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                '&:hover': { color: 'primary.main' }
                                              }}
                                            >
                                              <PlaceIcon fontSize="small" sx={{ mr: 0.5 }} />
                                              {activity.location}
                                            </Link>
                                          </Tooltip>
                                        ) : (
                                          <>
                                            <PlaceIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                            {activity.location}
                                          </>
                                        )}
                                      </Box>
                                    )}
                                  </Typography>
                                  <Typography variant="body1" sx={{ mt: 1 }}>
                                    {activity.description}
                                  </Typography>
                                  {activity.cost && (
                                    <Chip
                                      icon={<AttachMoneyIcon />}
                                      label={activity.cost}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ mt: 1, mr: 1, borderRadius: 0 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    ))
                  ) : (
                    <Alert severity="info" sx={{ my: 2, mx: 2 }}>
                      No activities found for this day. Try regenerating the itinerary.
                    </Alert>
                  )}
              </Grid>
              
              <Grid item xs={12} md={4}>
                {/* Booking Resources */}
                <Paper elevation={0} sx={{ 
                  p: 3, 
                  borderRadius: 0, 
                  mb: 4, 
                  background: 'linear-gradient(135deg, rgba(94, 23, 235, 0.08) 0%, rgba(94, 23, 235, 0.16) 100%)',
                  border: '1px solid rgba(94, 23, 235, 0.2)',
                  boxShadow: '0 8px 32px rgba(94, 23, 235, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
                    Book Your Trip
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Find the best deals for your trip to {itinerary.destination}:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(itinerary.destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<HotelIcon />}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          backgroundColor: '#003580', // Booking.com blue
                          '&:hover': { backgroundColor: '#00224f' }
                        }}
                      >
                        Hotels
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`https://www.kayak.com/flights/to/${encodeURIComponent(itinerary.destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<FlightIcon />}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          backgroundColor: '#FF690F', // Kayak orange
                          '&:hover': { backgroundColor: '#e05c0d' }
                        }}
                      >
                        Flights
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`https://www.opentable.com/s?covers=2&dateTime=2023-01-01T19%3A00%3A00&term=${encodeURIComponent(itinerary.destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<RestaurantIcon />}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          backgroundColor: '#da3743', // OpenTable red
                          '&:hover': { backgroundColor: '#b32b35' }
                        }}
                      >
                        Restaurants
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`https://www.viator.com/search/${encodeURIComponent(itinerary.destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LocalActivityIcon />}
                        sx={{ 
                          textTransform: 'none', 
                          justifyContent: 'flex-start',
                          backgroundColor: '#4EB978', // Viator green
                          '&:hover': { backgroundColor: '#3e9962' }
                        }}
                      >
                        Activities
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Featured Photo */}
                <Card elevation={0} sx={{ 
                  borderRadius: 0, 
                  overflow: 'hidden',
                  mb: 4,
                  background: 'linear-gradient(135deg, rgba(255, 153, 0, 0.08) 0%, rgba(255, 153, 0, 0.16) 100%)',
                  border: '1px solid rgba(255, 153, 0, 0.2)',
                  boxShadow: '0 8px 32px rgba(255, 153, 0, 0.1)'
                }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={destinationImage}
                    alt={itinerary?.destination || 'Travel destination'}
                  />
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {itinerary.destination} Highlights
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Explore the best that {itinerary.destination} has to offer with our carefully curated itinerary.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
        
      {/* Travel Information Section */}
      {itinerary.travelInfo && (
        <Box sx={{ mt: 6, '@media print': { pageBreakBefore: 'always' } }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Essential Travel Information
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 0, 
              border: '1px solid rgba(25, 118, 210, 0.2)',
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.12) 100%)',
              boxShadow: '0 8px 32px rgba(25, 118, 210, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Grid container spacing={3}>
              {itinerary.travelInfo.visaRequirements && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Visa Requirements
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.visaRequirements}
                  </Typography>
                </Grid>
              )}
              
              {itinerary.travelInfo.travelRestrictions && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Travel Restrictions
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.travelRestrictions}
                  </Typography>
                </Grid>
              )}
              
              {itinerary.travelInfo.healthRequirements && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Health Requirements
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.healthRequirements}
                  </Typography>
                </Grid>
              )}
              
              {itinerary.travelInfo.currency && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Currency Information
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.currency}
                  </Typography>
                </Grid>
              )}
              
              {itinerary.travelInfo.bestTimeToVisit && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Best Time to Visit
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.bestTimeToVisit}
                  </Typography>
                </Grid>
              )}
              
              {itinerary.travelInfo.localCustoms && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    Local Customs
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {itinerary.travelInfo.localCustoms}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Generate Itinerary CTA */}
      {!loading && !itinerary && !error && !isSharedMode && !isSavedView && (
        <Container maxWidth="md">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              mt: 4
            }}
          >
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Ready to Plan Your Trip?
            </Typography>
            <Typography variant="body1" paragraph>
              Click the button below to generate your personalized itinerary for {tripDetails.destination}.
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              onClick={generateItinerary}
              startIcon={<AutoAwesomeIcon />}
              sx={{ mt: 2 }}
            >
              Generate Itinerary
            </Button>
          </Paper>
        </Container>
      )}
    </Container>
    
    {/* Edit Trip Details Dialog */}
    <Dialog 
      open={editDialogOpen} 
      onClose={handleEditDialogClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ 
        sx: { 
          minHeight: '70vh',
          maxHeight: '90vh'
        } 
      }}
    >
      <DialogTitle>
        Edit Trip Details
        <IconButton
          aria-label="close"
          onClick={handleEditDialogClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Make changes to your trip details below to regenerate your itinerary.
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Destination"
              value={tripDetails.destination}
              fullWidth
              margin="normal"
              variant="outlined"
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <DateRangePicker
              startDate={tripDetails.startDate}
              endDate={tripDetails.endDate}
              onChange={handleDateRangeChange}
              touched={formTouched.startDate || formTouched.endDate}
              errors={formErrors}
              label="Travel Dates"
              maxDays={14}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Adults"
                type="number"
                value={tripDetails.adults}
                onChange={(e) => handleTripDetailsChange('adults', Math.max(1, parseInt(e.target.value) || 1))}
                InputProps={{ inputProps: { min: 1 } }}
              />
              <TextField
                fullWidth
                label="Children"
                type="number"
                value={tripDetails.children}
                onChange={(e) => handleTripDetailsChange('children', Math.max(0, parseInt(e.target.value) || 0))}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="budget-label">Budget</InputLabel>
              <Select
                labelId="budget-label"
                value={tripDetails.budget || 'medium'}
                label="Budget"
                onChange={(e) => handleTripDetailsChange('budget', e.target.value)}
              >
                <MenuItem value="budget">Budget</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="luxury">Luxury</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Interests"
              placeholder="Food & Dining, History & Culture, etc."
              value={interestsString}
              onChange={(e) => setInterestsString(e.target.value)}
              helperText="Separate interests with commas"
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Alert 
            severity="info" 
            sx={{ 
              '& .MuiAlert-message': { fontSize: '0.75rem' },
              '& .MuiAlertTitle-root': { fontSize: '0.80rem' }
            }}
          >
            Regenerating this itinerary will not consume any additional tokens.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleEditDialogClose} color="primary">
          Cancel
        </Button>
        <Button 
          onClick={handleSaveChanges} 
          color="primary"
          variant="contained"
        >
          Update and Regenerate
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Share Dialog */}
    {!isSharedMode && (
      <Dialog 
        open={shareDialogOpen} 
        onClose={handleShareDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Share Your {itinerary?.destination} Itinerary
          <IconButton
            aria-label="close"
            onClick={handleShareDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate a link to share this itinerary with friends and family. They'll be able to view all the details
            without needing to log in.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="share-expiry-label">Link Expires</InputLabel>
                  <Select
                    labelId="share-expiry-label"
                    value={shareLinkExpiry}
                    label="Link Expires"
                    onChange={(e) => setShareLinkExpiry(e.target.value)}
                  >
                    <MenuItem value="1d">24 hours</MenuItem>
                    <MenuItem value="7d">7 days</MenuItem>
                    <MenuItem value="30d">30 days</MenuItem>
                    <MenuItem value="never">Never</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={8}>
                <Button 
                  variant="contained" 
                  onClick={handleGenerateShareLink}
                  disabled={shareLoading}
                  startIcon={shareLoading ? <CircularProgress size={20} /> : <IosShareIcon />}
                  fullWidth
                >
                  {shareLink ? 'Regenerate Link' : 'Generate Share Link'}
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {shareLink && (
            <>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={shareLink}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton edge="end" onClick={handleCopyShareLink}>
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
              
              <Divider sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Share via
                </Typography>
              </Divider>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                <IconButton onClick={() => handleShareSocial('whatsapp')} sx={{ color: '#25D366' }}>
                  <WhatsAppIcon />
                </IconButton>
                <IconButton onClick={() => handleShareSocial('telegram')} sx={{ color: '#0088cc' }}>
                  <TelegramIcon />
                </IconButton>
                <IconButton onClick={() => handleShareSocial('facebook')} sx={{ color: '#3b5998' }}>
                  <FacebookIcon />
                </IconButton>
                <IconButton onClick={() => handleShareSocial('twitter')} sx={{ color: '#1DA1F2' }}>
                  <TwitterIcon />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    )}
    
    {/* Error Alert */}
    {!isSharedMode && saveStatus.error && (
      <Alert 
        severity="error" 
        sx={{ 
          position: 'fixed', 
          bottom: 20, 
          left: '50%', 
          transform: 'translateX(-50%)',
          maxWidth: '80%',
          zIndex: 9999
        }}
        onClose={() => setSaveStatus(prev => ({ ...prev, error: null }))}
      >
        {saveStatus.error}
      </Alert>
    )}
    
    {/* Snackbar for share notifications */}
    <Snackbar
      open={shareSnackbarOpen}
      autoHideDuration={4000}
      onClose={() => setShareSnackbarOpen(false)}
      message={shareSnackbarMessage}
      action={
        <IconButton
          size="small"
          color="inherit"
          onClick={() => setShareSnackbarOpen(false)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </Box>
);
};

export default ItineraryPage; 